import React, { useState, useEffect } from 'react';
import { GoogleAdsService } from '@/services/google-ads.service';
import { GoogleAdsCampaignType, GoogleAdsCampaignStatus } from '@/models/google-ads.model';

interface DemandGenCampaignFormProps {
  accountId?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export const DemandGenCampaignForm: React.FC<DemandGenCampaignFormProps> = ({
  accountId,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    budgetAmount: '',
    budgetPeriod: 'DAILY' as 'DAILY' | 'FIXED_DAILY',
    startDate: '',
    endDate: '',
    audienceSignals: [] as string[],
    audienceSignalInput: '',
    creativeAssets: [] as string[],
    creativeAssetInput: '',
    description: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate naming examples based on business type
  const namingExamples = [
    'FairCare Employer Benefits - Q1 2024',
    'FairCare Healthcare Solutions - Summer Campaign',
    'FairCare Employee Wellness - Restaurant Owners',
    'FairCare Benefits Discovery - Healthcare Workers',
    'FairCare Provider Network - New Locations',
  ];

  const [selectedExample, setSelectedExample] = useState<string | null>(null);

  useEffect(() => {
    // Set default start date to today
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    
    setFormData(prev => ({
      ...prev,
      startDate: today.toISOString().split('T')[0],
      endDate: nextMonth.toISOString().split('T')[0],
    }));
  }, []);

  const handleUseExample = (example: string) => {
    setFormData(prev => ({ ...prev, name: example }));
    setSelectedExample(example);
  };

  const handleAddAudienceSignal = () => {
    if (formData.audienceSignalInput.trim()) {
      setFormData(prev => ({
        ...prev,
        audienceSignals: [...prev.audienceSignals, prev.audienceSignalInput.trim()],
        audienceSignalInput: '',
      }));
    }
  };

  const handleRemoveAudienceSignal = (index: number) => {
    setFormData(prev => ({
      ...prev,
      audienceSignals: prev.audienceSignals.filter((_, i) => i !== index),
    }));
  };

  const handleAddCreativeAsset = () => {
    if (formData.creativeAssetInput.trim()) {
      setFormData(prev => ({
        ...prev,
        creativeAssets: [...prev.creativeAssets, prev.creativeAssetInput.trim()],
        creativeAssetInput: '',
      }));
    }
  };

  const handleRemoveCreativeAsset = (index: number) => {
    setFormData(prev => ({
      ...prev,
      creativeAssets: prev.creativeAssets.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (!formData.name.trim()) {
        throw new Error('Campaign name is required');
      }
      if (!formData.budgetAmount || parseFloat(formData.budgetAmount) <= 0) {
        throw new Error('Valid budget amount is required');
      }
      if (!accountId) {
        throw new Error('Account ID is required. Please connect a Google Ads account first.');
      }

      await GoogleAdsService.createCampaign({
        accountId,
        name: formData.name.trim(),
        type: GoogleAdsCampaignType.DEMAND_GEN,
        status: GoogleAdsCampaignStatus.ENABLED,
        budget: {
          amount: parseFloat(formData.budgetAmount),
          currencyCode: 'USD',
          deliveryMethod: 'STANDARD' as const,
          period: formData.budgetPeriod,
        },
        startDate: formData.startDate ? new Date(formData.startDate) : undefined,
        endDate: formData.endDate ? new Date(formData.endDate) : undefined,
        isDemandGen: true,
        demandGenSettings: {
          audienceSignals: formData.audienceSignals,
          creativeAssets: formData.creativeAssets,
        },
      });

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err: any) {
      console.error('[DemandGenCampaignForm] Error creating campaign:', err);
      setError(err.message || 'Failed to create campaign');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Create Demand Gen Campaign</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Campaign Name with Examples */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Campaign Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., FairCare Employer Benefits - Q1 2024"
              required
            />
            <div className="mt-2">
              <p className="text-xs text-gray-500 mb-2">Naming Examples:</p>
              <div className="flex flex-wrap gap-2">
                {namingExamples.map((example, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleUseExample(example)}
                    className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                      selectedExample === example
                        ? 'bg-blue-100 border-blue-500 text-blue-800'
                        : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Budget */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="budgetAmount" className="block text-sm font-medium text-gray-700 mb-2">
                Budget Amount ($) *
              </label>
              <input
                type="number"
                id="budgetAmount"
                value={formData.budgetAmount}
                onChange={(e) => setFormData(prev => ({ ...prev, budgetAmount: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="50.00"
                min="1"
                step="0.01"
                required
              />
              <p className="mt-1 text-xs text-gray-500">Example: $50.00</p>
            </div>
            <div>
              <label htmlFor="budgetPeriod" className="block text-sm font-medium text-gray-700 mb-2">
                Budget Period *
              </label>
              <select
                id="budgetPeriod"
                value={formData.budgetPeriod}
                onChange={(e) => setFormData(prev => ({ ...prev, budgetPeriod: e.target.value as 'DAILY' | 'FIXED_DAILY' }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="DAILY">Daily Budget</option>
                <option value="FIXED_DAILY">Fixed Daily Budget</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Daily: Spend up to the amount daily. Fixed Daily: Spend exactly the amount daily.
              </p>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
                Start Date *
              </label>
              <input
                type="date"
                id="startDate"
                value={formData.startDate}
                onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                id="endDate"
                value={formData.endDate}
                onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                min={formData.startDate}
              />
            </div>
          </div>

          {/* Audience Signals */}
          <div>
            <label htmlFor="audienceSignals" className="block text-sm font-medium text-gray-700 mb-2">
              Audience Signals
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                id="audienceSignals"
                value={formData.audienceSignalInput}
                onChange={(e) => setFormData(prev => ({ ...prev, audienceSignalInput: e.target.value }))}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddAudienceSignal();
                  }
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., restaurant owners, healthcare workers"
              />
              <button
                type="button"
                onClick={handleAddAudienceSignal}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm font-medium"
              >
                Add
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Examples: restaurant owners, healthcare workers, small business owners, HR managers
            </p>
            {formData.audienceSignals.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.audienceSignals.map((signal, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {signal}
                    <button
                      type="button"
                      onClick={() => handleRemoveAudienceSignal(idx)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Creative Assets */}
          <div>
            <label htmlFor="creativeAssets" className="block text-sm font-medium text-gray-700 mb-2">
              Creative Assets (URLs or IDs)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                id="creativeAssets"
                value={formData.creativeAssetInput}
                onChange={(e) => setFormData(prev => ({ ...prev, creativeAssetInput: e.target.value }))}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCreativeAsset();
                  }
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., https://example.com/video.mp4 or asset-id-123"
              />
              <button
                type="button"
                onClick={handleAddCreativeAsset}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm font-medium"
              >
                Add
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Examples: Video URLs, image URLs, or asset IDs from Google Ads
            </p>
            {formData.creativeAssets.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.creativeAssets.map((asset, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                  >
                    {asset.length > 50 ? `${asset.substring(0, 50)}...` : asset}
                    <button
                      type="button"
                      onClick={() => handleRemoveCreativeAsset(idx)}
                      className="ml-2 text-purple-600 hover:text-purple-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Campaign Description (Optional)
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Describe the campaign goals and target audience..."
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Creating...' : 'Create Campaign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

